import {
  type BookingDto,
  type BookingStatus,
  LOCALES,
  type Page,
  type PractitionerDto,
  type ServiceDto,
} from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api";
import { fieldErrors, hasFieldErrors } from "../forms";
import { addDays, formatDate, formatTime, localDate, useT } from "../i18n";
import { useMe } from "../session";
import { clinicDateTimeToIso, zonedToUtc } from "../time";
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner, Textarea } from "../ui";
import { useClinic } from "./app";

const STATUS_TONE: Record<BookingStatus, "green" | "red" | "gray" | "amber"> = {
  confirmed: "green",
  cancelled: "red",
  completed: "gray",
  no_show: "amber",
};

export function BookingsPage() {
  const { t, tError, locale } = useT();
  const qc = useQueryClient();
  const clinic = useClinic();
  const tz = clinic.data?.timezone;
  const [date, setDate] = useState(() => localDate(Date.now()));
  const [practitionerId, setPractitionerId] = useState("");
  const [status, setStatus] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const practitioners = useQuery({
    queryKey: ["practitioners", { showInactive: true }],
    queryFn: () => api<Page<PractitionerDto>>("GET", "/practitioners?limit=100&includeInactive=true"),
  });
  const services = useQuery({
    queryKey: ["services", { showInactive: false }],
    queryFn: () => api<Page<ServiceDto>>("GET", "/services?limit=100"),
  });

  const from = tz ? new Date(zonedToUtc(date, 0, tz)).toISOString() : null;
  const to = tz ? new Date(zonedToUtc(addDays(date, 1), 0, tz)).toISOString() : null;
  const list = useQuery({
    queryKey: ["bookings", { from, to, practitionerId, status }],
    enabled: Boolean(from && to),
    queryFn: () =>
      api<Page<BookingDto>>(
        "GET",
        `/bookings?from=${from}&to=${to}&limit=100${practitionerId ? `&practitionerId=${practitionerId}` : ""}${status ? `&status=${status}` : ""}`,
      ),
  });
  const refresh = (message: string) => {
    qc.invalidateQueries({ queryKey: ["bookings"] });
    setNotice(message);
  };

  const practitionerName = (id: string) => practitioners.data?.items.find((p) => p.id === id)?.name ?? "";

  return (
    <div className="space-y-6">
      <PageHeader title={t("bookings.title")} subtitle={t("bookings.subtitle")}>
        <Button type="button" variant={showNew ? "secondary" : "primary"} onClick={() => setShowNew((v) => !v)}>
          {showNew ? t("common.close") : t("bookings.new_booking")}
        </Button>
      </PageHeader>

      {showNew && clinic.data && services.data && practitioners.data ? (
        <NewBookingForm
          timezone={clinic.data.timezone}
          defaultLocale={clinic.data.defaultLocale}
          services={services.data.items}
          practitioners={practitioners.data.items.filter((p) => p.active)}
          onCreated={(b) => {
            setShowNew(false);
            setDate(localDate(b.startsAt, tz));
            refresh(t("bookings.created"));
          }}
        />
      ) : null}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-end gap-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDate(addDays(date, -1))}
              aria-label={t("common.previous")}
            >
              ‹
            </Button>
            <Field
              label={t("bookings.date")}
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDate(addDays(date, 1))}
              aria-label={t("common.next")}
            >
              ›
            </Button>
            <Button type="button" variant="ghost" onClick={() => setDate(localDate(Date.now(), tz))}>
              {t("common.today")}
            </Button>
          </div>
          <Select
            label={t("bookings.practitioner")}
            value={practitionerId}
            onChange={(e) => setPractitionerId(e.target.value)}
          >
            <option value="">{t("bookings.all_practitioners")}</option>
            {practitioners.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Select label={t("bookings.status")} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t("bookings.all_statuses")}</option>
            {(["confirmed", "cancelled", "completed", "no_show"] as const).map((s) => (
              <option key={s} value={s}>
                {t(`bookings.status_${s}`)}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <Card className="p-0">
        <h2 className="px-6 pt-5 font-semibold">
          {formatDate(locale, `${date}T12:00:00Z`, "UTC", { year: "numeric" })}
        </h2>
        {list.isPending || !tz ? (
          <div className="flex justify-center p-8 text-brand-700">
            <Spinner />
          </div>
        ) : list.isError ? (
          <div className="p-6">
            <Alert>{t("bookings.load_failed")}</Alert>
          </div>
        ) : list.data.items.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">{t("bookings.empty")}</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {list.data.items.map((b) => (
              <BookingRow
                key={b.id}
                booking={b}
                timezone={tz}
                practitioners={
                  practitioners.data?.items.filter((p) => p.active && p.serviceIds.includes(b.service.id)) ?? []
                }
                onChanged={refresh}
                tError={tError}
              />
            ))}
          </ul>
        )}
      </Card>
      {practitioners.data && practitioners.data.items.length > 0 && practitionerId ? (
        <p className="text-sm text-slate-500">{t("bookings.with", { name: practitionerName(practitionerId) })}</p>
      ) : null}
    </div>
  );
}

function BookingRow({
  booking: b,
  timezone,
  practitioners,
  onChanged,
  tError,
}: {
  booking: BookingDto;
  timezone: string;
  practitioners: PractitionerDto[];
  onChanged: (message: string) => void;
  tError: (err: unknown) => string;
}) {
  const { t, locale } = useT();
  const [mode, setMode] = useState<"idle" | "move" | "cancel" | "customer">("idle");
  const me = useMe();
  const role = me.data?.memberships.find((m) => m.clinicId === me.data?.activeClinicId)?.role;
  // Export and erasure (GDPR art. 15, 17, 20) are for owners and administrators.
  const canManageData = (role === "owner" || role === "admin") && !b.customer.email.endsWith("@invalid");
  const exportData = useMutation({
    mutationFn: () => api<unknown>("GET", `/customers/${b.customer.id}/export`),
    onSuccess: (data) => {
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: `customer-${b.customer.id}.json` });
      a.click();
      URL.revokeObjectURL(url);
    },
  });
  const erase = useMutation({
    mutationFn: () => api<void>("POST", `/customers/${b.customer.id}/erase`),
    onSuccess: () => {
      setMode("idle");
      onChanged(t("bookings.customer_erased"));
    },
  });
  const patch = useMutation({
    mutationFn: (status: "completed" | "no_show") => api<BookingDto>("PATCH", `/bookings/${b.id}`, { status }),
    onSuccess: () => onChanged(t("bookings.updated")),
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => api<BookingDto>("POST", `/bookings/${b.id}/cancel`, { reason }),
    onSuccess: () => {
      setMode("idle");
      onChanged(t("bookings.cancelled"));
    },
  });
  const move = useMutation({
    mutationFn: (input: { startsAt: string; practitionerId?: string }) =>
      api<BookingDto>("POST", `/bookings/${b.id}/reschedule`, input),
    onSuccess: () => {
      setMode("idle");
      onChanged(t("bookings.moved"));
    },
  });
  const error = patch.error ?? cancel.error ?? move.error ?? exportData.error ?? erase.error;

  return (
    <li className="px-6 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {formatTime(locale, b.startsAt, timezone)}–{formatTime(locale, b.endsAt, timezone)} · {b.service.name}
            <span className="ml-2">
              <Badge tone={STATUS_TONE[b.status]}>{t(`bookings.status_${b.status}`)}</Badge>
            </span>
          </p>
          <p className="text-sm text-slate-700">
            {b.customer.fullName} ·{" "}
            <a href={`mailto:${b.customer.email}`} className="hover:underline">
              {b.customer.email}
            </a>
            {b.customer.phone ? ` · ${b.customer.phone}` : ""}
          </p>
          <p className="text-sm text-slate-500">
            {t("bookings.with", { name: b.practitioner.name })} ·{" "}
            {b.source === "online" ? t("bookings.source_online") : t("bookings.source_staff")}
            {b.rescheduledAt ? ` · ${t("bookings.moved_from")}` : ""}
            {b.cancellationReason ? ` · ${b.cancellationReason}` : ""}
          </p>
          {b.notes ? <p className="mt-1 text-sm text-slate-600 italic">{b.notes}</p> : null}
        </div>
        {b.status === "confirmed" || canManageData ? (
          <div className="flex flex-wrap gap-2">
            {b.status === "confirmed" ? (
              <>
                <Button type="button" variant="secondary" onClick={() => setMode(mode === "move" ? "idle" : "move")}>
                  {t("bookings.move")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  busy={patch.isPending && patch.variables === "completed"}
                  onClick={() => patch.mutate("completed")}
                >
                  {t("bookings.mark_completed")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  busy={patch.isPending && patch.variables === "no_show"}
                  onClick={() => patch.mutate("no_show")}
                >
                  {t("bookings.mark_no_show")}
                </Button>
              </>
            ) : null}
            {b.status === "confirmed" ? (
              <Button type="button" variant="ghost" onClick={() => setMode(mode === "cancel" ? "idle" : "cancel")}>
                {t("bookings.cancel_booking")}
              </Button>
            ) : null}
            {canManageData ? (
              <Button type="button" variant="ghost" onClick={() => setMode(mode === "customer" ? "idle" : "customer")}>
                {t("bookings.customer_data")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {error ? (
        <div className="mt-3">
          <Alert>{tError(error)}</Alert>
        </div>
      ) : null}
      {mode === "customer" ? (
        <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-4 text-sm">
          <p>{t("bookings.customer_data_text", { name: b.customer.fullName })}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" busy={exportData.isPending} onClick={() => exportData.mutate()}>
              {t("bookings.customer_export")}
            </Button>
            <Button
              type="button"
              variant="danger"
              busy={erase.isPending}
              onClick={() => {
                if (window.confirm(t("bookings.customer_erase_confirm", { name: b.customer.fullName }))) erase.mutate();
              }}
            >
              {t("bookings.customer_erase")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setMode("idle")}>
              {t("common.back")}
            </Button>
          </div>
        </div>
      ) : null}
      {mode === "cancel" ? (
        <form
          className="mt-3 flex flex-wrap items-end gap-3 rounded-lg bg-slate-50 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            cancel.mutate(String(new FormData(e.currentTarget).get("reason") ?? ""));
          }}
        >
          <p className="w-full text-sm">{t("bookings.cancel_confirm")}</p>
          <div className="min-w-64 flex-1">
            <Field label={t("bookings.cancel_reason")} name="reason" />
          </div>
          <Button busy={cancel.isPending}>{t("bookings.cancel_booking")}</Button>
          <Button type="button" variant="ghost" onClick={() => setMode("idle")}>
            {t("common.back")}
          </Button>
        </form>
      ) : null}
      {mode === "move" ? (
        <form
          className="mt-3 grid gap-3 rounded-lg bg-slate-50 p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
            move.mutate({
              startsAt: clinicDateTimeToIso(v.date ?? "", v.time ?? "", timezone),
              practitionerId: v.practitionerId || undefined,
            });
          }}
        >
          <p className="text-sm sm:col-span-4">{t("bookings.move_hint")}</p>
          <Field
            label={t("bookings.date")}
            name="date"
            type="date"
            defaultValue={localDate(b.startsAt, timezone)}
            required
          />
          <Field
            label={t("bookings.time")}
            name="time"
            type="time"
            step={300}
            defaultValue={formatTime("en", b.startsAt, timezone)}
            required
          />
          <Select label={t("bookings.practitioner")} name="practitionerId" defaultValue={b.practitioner.id}>
            {practitioners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <div className="flex items-end gap-2">
            <Button busy={move.isPending}>{t("bookings.move_button")}</Button>
            <Button type="button" variant="ghost" onClick={() => setMode("idle")}>
              {t("common.back")}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

function NewBookingForm({
  timezone,
  defaultLocale,
  services,
  practitioners,
  onCreated,
}: {
  timezone: string;
  defaultLocale: string;
  services: ServiceDto[];
  practitioners: PractitionerDto[];
  onCreated: (b: BookingDto) => void;
}) {
  const { t, tError } = useT();
  const [serviceId, setServiceId] = useState(services[0]?.id ?? "");
  const eligible = practitioners.filter((p) => p.serviceIds.includes(serviceId));
  const create = useMutation({
    mutationFn: (input: unknown) => api<BookingDto>("POST", "/bookings", input),
    onSuccess: onCreated,
  });
  const fields = fieldErrors(create.error);

  return (
    <Card>
      <h2 className="font-semibold">{t("bookings.new_booking")}</h2>
      <p className="mt-1 text-sm text-slate-600">{t("bookings.new_hint")}</p>
      <form
        className="mt-4 grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
          create.mutate({
            serviceId: v.serviceId,
            practitionerId: v.practitionerId,
            startsAt: clinicDateTimeToIso(v.date ?? "", v.time ?? "", timezone),
            customer: { fullName: v.fullName, email: v.email, phone: v.phone ?? "" },
            notes: v.notes ?? "",
            locale: v.locale,
            sendConfirmation: v.sendConfirmation === "on",
          });
        }}
      >
        {create.error && !hasFieldErrors(create.error) ? (
          <div className="sm:col-span-2">
            <Alert>{tError(create.error)}</Alert>
          </div>
        ) : null}
        <Select
          label={t("bookings.service")}
          name="serviceId"
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
          error={fields.serviceId}
        >
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({t("common.minutes", { n: s.durationMinutes })})
            </option>
          ))}
        </Select>
        <Select label={t("bookings.practitioner")} name="practitionerId" error={fields.practitionerId} required>
          {eligible.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Field
          label={t("bookings.date")}
          name="date"
          type="date"
          defaultValue={localDate(Date.now(), timezone)}
          required
          error={fields.startsAt}
        />
        <Field label={t("bookings.time")} name="time" type="time" step={300} defaultValue="09:00" required />
        <Field label={t("bookings.customer_name")} name="fullName" required error={fields["customer.fullName"]} />
        <Field
          label={t("bookings.customer_email")}
          name="email"
          type="email"
          required
          error={fields["customer.email"]}
        />
        <Field label={t("bookings.customer_phone")} name="phone" error={fields["customer.phone"]} />
        <Select label={t("bookings.language")} name="locale" defaultValue={defaultLocale}>
          {LOCALES.map((l) => (
            <option key={l} value={l}>
              {t(`lang.${l}`)}
            </option>
          ))}
        </Select>
        <div className="sm:col-span-2">
          <Textarea label={t("bookings.notes")} name="notes" error={fields.notes} />
        </div>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" name="sendConfirmation" defaultChecked />
          {t("bookings.send_confirmation")}
        </label>
        <div className="sm:col-span-2">
          <Button busy={create.isPending}>{t("bookings.create_button")}</Button>
        </div>
      </form>
    </Card>
  );
}
